/**
 * Session-scoped registry of sidequests the player has authored via the
 * Sidequest Author editor (Variant B). See
 * `docs/superpowers/specs/2026-05-18-sidequest-author-editor-design.md`.
 *
 *  - `create` validates the wallet has ≥1 scroll, spends it, pushes a new
 *    entry. The entry starts in `spawnState.kind === "pending"`.
 *  - RunnerScene's RESUME listener drains pending entries on every editor
 *    close, calling `markPlaced` after the portal spawns in the world.
 *  - On portal acceptance, RunnerScene calls `markEntered`; the entry stays
 *    in the list so the parchment map can render its visited pin.
 *
 * Singleton — registry-backed under `authoredSidequestService`. Created
 * lazily via `getOrCreate` from the first consumer scene (LauncherScene if
 * we ever surface a Launcher-side entry; today only RunnerScene + the
 * editor scene).
 */

import Phaser from "phaser";

import { GAMEPLAY } from "../config";
import type {
  AuthoredSidequest,
  AuthoredSidequestInput,
} from "../types";

import { hashString } from "./seededRng";
import type { Wallet } from "./Wallet";

const REGISTRY_KEY = "authoredSidequestService";

export interface AuthoredSidequestServiceDeps {
  game: Phaser.Game;
  /** Layer-1 / 2 telemetry emit. */
  emitTelemetry: (
    layer: 1 | 2 | 3,
    tag: string,
    data?: Record<string, number | string | boolean>,
  ) => void;
  /** Current ms-since-session-start; callers pass their scene clock. */
  now: () => number;
}

export interface AuthoredSidequestDebugInfo {
  totalAuthored: number;
  pendingCount: number;
  placedCount: number;
  enteredCount: number;
}

export type AuthoredSidequestListener = (sidequests: readonly AuthoredSidequest[]) => void;

export class AuthoredSidequestService {
  private readonly entries: AuthoredSidequest[] = [];
  private readonly listeners = new Set<AuthoredSidequestListener>();
  private counter = 0;

  constructor(private readonly deps: AuthoredSidequestServiceDeps) {}

  static getOrCreate(deps: AuthoredSidequestServiceDeps): AuthoredSidequestService {
    const existing = deps.game.registry.get(REGISTRY_KEY) as AuthoredSidequestService | undefined;
    if (existing) return existing;
    const inst = new AuthoredSidequestService(deps);
    deps.game.registry.set(REGISTRY_KEY, inst);
    return inst;
  }

  // -- public API -----------------------------------------------------------

  /** Newest first (matches the parchment map ordering). */
  list(): readonly AuthoredSidequest[] {
    return this.entries;
  }

  /**
   * Validates the wallet has ≥1 scroll, spends it, registers the sidequest.
   * Throws if the wallet can't afford the scroll cost (the UI gates this
   * separately; the throw is defense-in-depth against a race between the
   * focus check and the activation).
   */
  create(input: AuthoredSidequestInput, wallet: Wallet): AuthoredSidequest {
    if (!input.authorUsername.trim()) throw new Error("authorUsername empty");
    if (!input.name.trim()) throw new Error("name empty");
    const cost = { inventory: { remove: ["scroll"] as string[] } };
    if (!wallet.canAfford(cost)) throw new Error("no scroll to spend");
    wallet.apply(cost);

    this.counter += 1;
    const id = `auth-${String(this.counter).padStart(4, "0")}`;
    const entry: AuthoredSidequest = {
      ...input,
      id,
      authoredAtMs: this.deps.now(),
      authorAvatarSeed: hashString(`author:${input.authorUsername}`),
      spawnState: { kind: "pending" },
    };
    this.entries.unshift(entry);

    this.deps.emitTelemetry(2, "ugc.sidequest.authored", {
      id: entry.id,
      name: entry.name,
      puzzleKind: entry.puzzleKind,
      biome: entry.biome,
      setPieceIds: entry.setPieceIds.join(","),
      authorUsername: entry.authorUsername,
    });
    this.notify();
    return entry;
  }

  /** Pending sidequests waiting to be spawned in the world. */
  pendingForDepth(_depth: number): readonly AuthoredSidequest[] {
    // Depth is currently ignored — pending sidequests always spawn at the
    // current depth when the editor closes. The depth field exists so we
    // can record where the portal was placed (for map rendering) and so a
    // future change can pre-target a different depth. Underscore-prefixed
    // to silence the unused-arg lint while keeping the signature stable
    // for callers.
    return this.entries.filter((e) => e.spawnState.kind === "pending");
  }

  markPlaced(id: string, worldX: number, depth: number): void {
    const entry = this.entries.find((e) => e.id === id);
    if (!entry) return;
    if (entry.spawnState.kind !== "pending") return;
    entry.spawnState = { kind: "placed", worldX, depth };
    this.deps.emitTelemetry(2, "ugc.sidequest.placed", { id, worldX, depth });
    this.notify();
  }

  markEntered(id: string): void {
    const entry = this.entries.find((e) => e.id === id);
    if (!entry) return;
    if (entry.spawnState.kind === "entered") return;
    const depth = entry.spawnState.kind === "placed" ? entry.spawnState.depth : 0;
    entry.spawnState = { kind: "entered", enteredAtMs: this.deps.now(), depth };
    this.deps.emitTelemetry(2, "ugc.sidequest.entered", {
      id,
      name: entry.name,
      authorUsername: entry.authorUsername,
      depth,
    });
    this.notify();
  }

  subscribe(fn: AuthoredSidequestListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  debugInfo(): AuthoredSidequestDebugInfo {
    let pending = 0;
    let placed = 0;
    let entered = 0;
    for (const e of this.entries) {
      if (e.spawnState.kind === "pending") pending += 1;
      else if (e.spawnState.kind === "placed") placed += 1;
      else entered += 1;
    }
    return {
      totalAuthored: this.entries.length,
      pendingCount: pending,
      placedCount: placed,
      enteredCount: entered,
    };
  }

  /** Cost in scrolls per sidequest (constant; pulled from GAMEPLAY for tests). */
  static readonly SCROLL_COST = GAMEPLAY.sidequestAuthorScrollCost;

  // -- internals ------------------------------------------------------------

  private notify(): void {
    for (const fn of this.listeners) fn(this.entries);
  }
}
